import { routes } from '@semios/app-platform-banyan-route-definitions'
import { createKeyedFetchCache } from 'App/dashboards/_utils/createKeyedFetchCache'
import type { TPlanningProperty } from 'App/dashboards/_utils/planningBatchRequest'
import {
  buildBatchRequest,
  groupWithinLimits,
  splitBatchResponse,
} from 'App/dashboards/_utils/planningBatchRequest'
import { useEffect, useMemo, useRef, useState } from 'react'
import { apiFetch } from 'utils/apiFetch'
import { captureFrontendError } from 'utils/captureFrontendError'
import { ApiResponseError } from 'utils/errorCodes'

type TPlanningResponse = routes.IrrigationSchedulerPlanningData.Response

type TPlanningZone = TPlanningResponse['dailyEt'][number]

type TPlanningRequestKey = TPlanningProperty & { dateFrom: string; dateTo: string }

// Each call runs a BigQuery job and a precipitation lookup on the server, so keep only a few in flight.
const PLANNING_DATA_CONCURRENCY = 4
const PLANNING_BATCH_SIZE = 20
// A selection change reuses what is already loaded, so this bounds how old a reused result can be in a
// tab left open. The header Refresh clears it regardless.
const PLANNING_DATA_TTL_MS = 15 * 60 * 1000
const PLANNING_DATA_TIMEOUT_MS = 2 * 60 * 1000

// The cache key is the request itself, so a change to a property's blocks or zones, or to the window,
// is a different key and is fetched again.
const toEntry = (key: string) => {
  const request: TPlanningRequestKey = JSON.parse(key)

  return { key, ...request }
}

const toBatchRequests = (keys: string[]) =>
  groupWithinLimits(keys.map(toEntry), {
    maxItems: PLANNING_BATCH_SIZE,
    maxZones: routes.IrrigationSchedulerPlanningData.MAX_IRRIGATION_ZONE_IDS,
    maxBlocks: routes.IrrigationSchedulerPlanningData.MAX_BLOCK_IDS,
  }).map((group) => group.map((entry) => entry.key))

const fetchPlanningData = async ({
  keys,
  dateFrom,
  dateTo,
}: {
  keys: string[]
  dateFrom: string
  dateTo: string
}) => {
  const entries = keys.map(toEntry)

  try {
    const response = await apiFetch({
      url: routes.IrrigationSchedulerPlanningData.path,
      body: buildBatchRequest({ properties: entries, dateFrom, dateTo }),
      params: { timeout: PLANNING_DATA_TIMEOUT_MS },
    })

    return splitBatchResponse(entries, response)
  } catch (error) {
    // A 403 means the user holds none of the planner / weather / ETc permissions on the property, which
    // irrigation access doesn't imply. Rain and ETc stay blank; nothing to report.
    if (!(error instanceof ApiResponseError && error.statusCode === 403)) {
      captureFrontendError(error, {
        extra: {
          message: 'Water Monitoring planning-data fetch failed',
          propertyIds: entries.map((entry) => entry.propertyId),
        },
      })
    }

    throw error
  }
}

const fetchOne = async (key: string): Promise<TPlanningResponse> => {
  const { dateFrom, dateTo } = toEntry(key)
  const values = await fetchPlanningData({ keys: [key], dateFrom, dateTo })

  return values.get(key) ?? { dailyEt: [] }
}

// Rain and ETc per irrigation zone, kept per property. `refreshKey` changing drops every result and
// refetches the selection.
export const usePlanningDataByProperty = ({
  properties,
  dateFrom,
  dateTo,
  refreshKey,
}: {
  properties: TPlanningProperty[]
  dateFrom: string
  dateTo: string
  refreshKey: string
}) => {
  const cache = useMemo(
    () =>
      createKeyedFetchCache<TPlanningResponse>({
        fetchValue: fetchOne,
        concurrency: PLANNING_DATA_CONCURRENCY,
        ttlMs: PLANNING_DATA_TTL_MS,
      }),
    [],
  )

  const [zonesByPropertyId, setZonesByPropertyId] = useState<Record<number, TPlanningZone[]>>({})
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null)
  const wantedKeysRef = useRef<Set<string>>(new Set())
  const refreshKeyRef = useRef(refreshKey)

  useEffect(() => {
    let cancelled = false

    if (refreshKeyRef.current !== refreshKey) {
      refreshKeyRef.current = refreshKey

      cache.clear()

      setZonesByPropertyId({})
    }

    const requests = properties.map((property) => ({
      propertyId: property.propertyId,
      key: JSON.stringify({ ...property, dateFrom, dateTo } satisfies TPlanningRequestKey),
    }))

    wantedKeysRef.current = new Set(requests.map((request) => request.key))

    const isWanted = (key: string) => wantedKeysRef.current.has(key)

    const showZones = (propertyId: number, response: TPlanningResponse | undefined) => {
      // Undefined means the request was dropped before it started, because the selection moved on.
      if (cancelled || !response) return

      setZonesByPropertyId((previous) => ({ ...previous, [propertyId]: response.dailyEt }))

      setLastFetchedAt(new Date())
    }

    const responses = cache.requestMany(
      requests.map((request) => request.key),
      isWanted,
      (keys) => fetchPlanningData({ keys, dateFrom, dateTo }),
      toBatchRequests,
    )

    requests.forEach((request, index) => {
      responses[index]
        .then((response) => showZones(request.propertyId, response))
        .catch(() => {
          if (cancelled) return

          // fetchPlanningData already reported it.
          setZonesByPropertyId((previous) => ({ ...previous, [request.propertyId]: [] }))
        })
    })

    return () => {
      cancelled = true
    }
  }, [cache, properties, dateFrom, dateTo, refreshKey])

  const planningByZoneId = useMemo(
    () =>
      new Map(
        properties.flatMap((property) =>
          (zonesByPropertyId[property.propertyId] ?? []).map(
            (zone) => [zone.zone_id, zone.blockData] as const,
          ),
        ),
      ),
    [properties, zonesByPropertyId],
  )

  const pendingPropertyIds = useMemo(
    () =>
      new Set(
        properties
          .filter((property) => zonesByPropertyId[property.propertyId] === undefined)
          .map((property) => property.propertyId),
      ),
    [properties, zonesByPropertyId],
  )

  return { planningByZoneId, pendingPropertyIds, lastFetchedAt }
}
